import { useState, useEffect } from 'react';
import type { TriangleGroup, WeaponType } from '@oz/rules';
import {
  fetchAdminContent,
  saveAdminContent,
  deleteAdminContent,
  type AdminItem,
} from '../../admin/contentApi.js';
import { ContentOrigin } from '../../admin/ContentOrigin.js';
import { ARCHETYPES, CONDITION_OPTIONS, WEAPON_TYPE_OPTIONS, triangleGroupFor } from '../../admin/vocab.js';

export interface WeaponDef {
  readonly id: number;
  readonly name: string;
  readonly type: WeaponType;
  readonly triangleGroup: TriangleGroup | null;
  readonly might: number;
  readonly hit: number;
  readonly crit: number;
  readonly weight: number;
  readonly range: readonly [number, number];
  readonly magic: boolean;
  readonly heals: boolean;
  readonly uses: number;
  readonly price?: number;
  readonly icon?: number;
  readonly description?: string;
  readonly effectiveAgainst?: readonly string[];
  /** O que um golpe certeiro pode causar — a adaga envenenada. */
  readonly inflicts?: readonly { readonly condition: string; readonly chance: number }[];
  /** As condições que um cajado de cura remove de quem ele atende. */
  readonly cures?: readonly string[];
}

const WEAPON_TYPES = WEAPON_TYPE_OPTIONS;

/** "Eficaz contra" compara com o arquétipo da classe do alvo — então a lista é a dos arquétipos. */
const EFFECTIVE_TARGETS = ARCHETYPES;

export function WeaponEditor(): JSX.Element {
  const [items, setItems] = useState<AdminItem<WeaponDef>[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filterText, setFilterText] = useState('');
  const [filterType, setFilterType] = useState<string>('todas');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  // Form states
  const [name, setName] = useState('');
  const [type, setType] = useState<WeaponType>('espada');
  const [triangleGroup, setTriangleGroup] = useState<TriangleGroup | ''>('espada');
  const [might, setMight] = useState(5);
  const [hit, setHit] = useState(85);
  const [crit, setCrit] = useState(5);
  const [weight, setWeight] = useState(4);
  const [minRange, setMinRange] = useState(1);
  const [maxRange, setMaxRange] = useState(1);
  const [magic, setMagic] = useState(false);
  const [heals, setHeals] = useState(false);
  const [uses, setUses] = useState(40);
  const [price, setPrice] = useState(450);
  const [icon, setIcon] = useState(97);
  const [description, setDescription] = useState('');
  const [effectiveAgainst, setEffectiveAgainst] = useState<string[]>([]);
  /** A condição que o golpe pode deixar, e a chance dela. Vazio é nenhuma. */
  const [inflictCondition, setInflictCondition] = useState<string>('');
  const [inflictChance, setInflictChance] = useState(30);
  /** As condições que o cajado remove. */
  const [cures, setCures] = useState<string[]>([]);
  const [isNew, setIsNew] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await fetchAdminContent<WeaponDef>('weapons');
      setItems(data);
      if (data.length > 0 && selectedId === null && data[0]) {
        selectItem(data[0]);
      }
    } catch (e) {
      setMessage({ type: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const selectItem = (item: AdminItem<WeaponDef>) => {
    setSelectedId(item.contentId);
    setIsNew(false);
    setName(item.name || item.data.name || '');
    setType(item.data.type || 'espada');
    setTriangleGroup(item.data.triangleGroup ?? '');
    setMight(item.data.might ?? 5);
    setHit(item.data.hit ?? 85);
    setCrit(item.data.crit ?? 0);
    setWeight(item.data.weight ?? 4);
    setMinRange(item.data.range ? item.data.range[0] : 1);
    setMaxRange(item.data.range ? item.data.range[1] : 1);
    setMagic(Boolean(item.data.magic));
    setHeals(Boolean(item.data.heals));
    setUses(item.data.uses ?? 40);
    setPrice(item.data.price ?? 300);
    setIcon(item.data.icon ?? 97);
    setDescription(item.data.description ?? '');
    setEffectiveAgainst([...(item.data.effectiveAgainst ?? [])]);
    const inflict = item.data.inflicts?.[0];
    setInflictCondition(inflict?.condition ?? '');
    setInflictChance(inflict?.chance ?? 30);
    setCures([...(item.data.cures ?? [])]);
    setMessage(null);
  };

  const handleNew = () => {
    setSelectedId(null);
    setIsNew(true);
    setName('Nova Arma');
    setType('espada');
    setTriangleGroup('espada');
    setMight(6);
    setHit(85);
    setCrit(5);
    setWeight(4);
    setMinRange(1);
    setMaxRange(1);
    setMagic(false);
    setHeals(false);
    setUses(45);
    setPrice(400);
    setIcon(97);
    setDescription('Uma nova lâmina forjada com esmero.');
    setEffectiveAgainst([]);
    setInflictCondition('');
    setInflictChance(30);
    setCures([]);
    setMessage(null);
  };

  const handleTypeChange = (newType: WeaponType) => {
    setType(newType);
    setTriangleGroup(triangleGroupFor(newType) ?? '');
    if (newType === 'arco') {
      setMinRange(2);
      setMaxRange(2);
    } else if (newType === 'cajado') {
      setHeals(true);
    }
  };

  const toggleEffective = (eff: string) => {
    if (effectiveAgainst.includes(eff)) {
      setEffectiveAgainst(effectiveAgainst.filter((e) => e !== eff));
    } else {
      setEffectiveAgainst([...effectiveAgainst, eff]);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setMessage({ type: 'err', text: 'O nome da arma é obrigatório.' });
      return;
    }

    setSaving(true);
    setMessage(null);

    /*
     * O que o formulário não mostra tem de sobreviver ao salvamento.
     *
     * A gravação substitui o registro inteiro pelo que sobe daqui, e há campos
     * que este formulário não edita — de qual jogo importado o registro veio,
     * de qual folha o ícone fala, e o que o conversor trouxe da origem.
     * Montando o pacote em cima da ficha atual, um ajuste de preço deixa de
     * apagá-los.
     */
    const existing = items.find((it) => it.contentId === selectedId)?.data;

    const payload: Partial<WeaponDef> & { name: string } = {
      ...(isNew || !existing ? {} : existing),
      name: name.trim(),
      type,
      triangleGroup: triangleGroup !== '' ? (triangleGroup as TriangleGroup) : null,
      might: Number(might),
      hit: Number(hit),
      crit: Number(crit),
      weight: Number(weight),
      range: [Number(minRange), Math.max(Number(minRange), Number(maxRange))],
      magic: Boolean(magic),
      heals: Boolean(heals),
      uses: Number(uses),
      price: Number(price),
      icon: Number(icon),
      description,
      effectiveAgainst,
      // Uma condição por arma basta para o que o jogo pede hoje, e mantém a
      // previsão de combate legível: "envenena 50%".
      inflicts: inflictCondition
        ? [{ condition: inflictCondition, chance: Math.max(1, Math.min(100, Number(inflictChance))) }]
        : [],
      // Curar condição só faz sentido em quem cura.
      cures: heals ? cures : [],
    };

    const targetId = isNew ? null : selectedId;
    const res = await saveAdminContent<WeaponDef>('weapons', targetId, payload);

    setSaving(false);
    if (!res.ok) {
      setMessage({ type: 'err', text: res.message ?? 'Erro ao salvar arma.' });
    } else {
      setMessage({ type: 'ok', text: `Arma "${name}" salva com sucesso!` });
      await loadData();
      if (res.item) {
        selectItem(res.item);
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!window.confirm(`Tem certeza que deseja excluir a arma ID #${selectedId} (${name})?`)) {
      return;
    }

    setSaving(true);
    const res = await deleteAdminContent('weapons', selectedId);
    setSaving(false);

    if (!res.ok) {
      setMessage({ type: 'err', text: res.message ?? 'Erro ao excluir arma.' });
    } else {
      setMessage({ type: 'ok', text: `Arma #${selectedId} excluída.` });
      const remaining = items.filter((it) => it.contentId !== selectedId);
      setItems(remaining);
      if (remaining.length > 0 && remaining[0]) {
        selectItem(remaining[0]);
      } else {
        handleNew();
      }
    }
  };

  const filteredItems = items.filter((it) => {
    const matchesText =
      it.name.toLowerCase().includes(filterText.toLowerCase()) ||
      String(it.contentId).includes(filterText) ||
      (it.data.type && it.data.type.toLowerCase().includes(filterText.toLowerCase()));
    const matchesCategory = filterType === 'todas' || it.data.type === filterType;
    return matchesText && matchesCategory;
  });

  const currentItem = items.find((it) => it.contentId === selectedId);

  return (
    <div className="admin-db-container">
      {/* Coluna Esquerda: Filtros e Lista Mestre */}
      <aside className="admin-sidebar-list">
        <div className="admin-list-header">
          <input
            type="text"
            className="admin-search-input"
            placeholder="🔍 Filtrar armas..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />

          <div className="admin-filter-pill-row">
            <button
              type="button"
              className={`filter-pill ${filterType === 'todas' ? 'active' : ''}`}
              onClick={() => setFilterType('todas')}
            >
              Todas
            </button>
            {WEAPON_TYPES.map((wt) => (
              <button
                key={wt.id}
                type="button"
                className={`filter-pill ${filterType === wt.id ? 'active' : ''}`}
                onClick={() => setFilterType(wt.id)}
                title={wt.label}
              >
                {wt.icon}
              </button>
            ))}
          </div>

          <button type="button" className="admin-btn-primary admin-btn-new" onClick={handleNew}>
            + Nova Arma
          </button>
        </div>

        {loading ? (
          <div className="admin-loading-indicator">Carregando armas…</div>
        ) : (
          <div className="admin-items-scroll">
            {filteredItems.map((it) => {
              const active = !isNew && it.contentId === selectedId;
              const typeIcon = WEAPON_TYPES.find((w) => w.id === it.data.type)?.icon ?? '⚔️';
              return (
                <button
                  key={it.contentId}
                  type="button"
                  className={`admin-list-item-btn ${active ? 'active' : ''}`}
                  onClick={() => selectItem(it)}
                >
                  <span className="admin-item-id">
                    {String(it.contentId).padStart(3, '0')}:
                  </span>
                  <span className="admin-item-weapon-icon">{typeIcon}</span>
                  <span className="admin-item-name">{it.name}</span>
                  {it.isCustom && <span className="admin-badge-custom">Custom</span>}
                  <span className="admin-item-meta">
                    Mt {it.data.might ?? 0} · Hit {it.data.hit ?? 0}%
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </aside>

      {/* Coluna Direita: Formulário Completo */}
      <main className="admin-main-form">
        <div className="admin-form-top-bar">
          <div className="admin-form-title-area">
            <h2>
              {isNew ? '✨ Criando Nova Arma' : `⚔️ Arma #${selectedId}: ${name}`}
            </h2>
            {currentItem && (
              <ContentOrigin item={currentItem} />
            )}
          </div>

          <div className="admin-form-actions">
            {!isNew && (
              <button
                type="button"
                className="admin-btn-danger"
                onClick={() => void handleDelete()}
                disabled={saving}
              >
                🗑️ Excluir
              </button>
            )}
            <button
              type="button"
              className="admin-btn-save"
              onClick={() => void handleSave()}
              disabled={saving}
            >
              {saving ? 'Salvando…' : '💾 Salvar Alterações'}
            </button>
          </div>
        </div>

        {message && (
          <div className={`admin-alert-banner ${message.type === 'ok' ? 'success' : 'error'}`}>
            {message.text}
          </div>
        )}

        <div className="admin-form-body-grid">
          {/* Caixa 1: Identificação e Categoria */}
          <div className="admin-section-box">
            <h3 className="admin-section-title">Identificação & Tipo</h3>

            <div className="admin-form-row">
              <label>Nome da Arma:</label>
              <input
                type="text"
                className="admin-input-text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Lança Sagrada de Naux"
              />
            </div>

            <div className="admin-grid-2col">
              <div className="admin-form-row">
                <label>Tipo de Arma:</label>
                <select
                  className="admin-select"
                  value={type}
                  onChange={(e) => handleTypeChange(e.target.value as WeaponType)}
                >
                  {WEAPON_TYPES.map((wt) => (
                    <option key={wt.id} value={wt.id}>
                      {wt.icon} {wt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="admin-form-row">
                <label>Grupo de Triângulo:</label>
                <select
                  className="admin-select"
                  value={triangleGroup}
                  onChange={(e) => setTriangleGroup(e.target.value as TriangleGroup | '')}
                >
                  <option value="">(Nenhum - Fora do Triângulo)</option>
                  <option value="espada">🗡️ Espada (Vence Machado)</option>
                  <option value="lanca">🔱 Lança (Vence Espada)</option>
                  <option value="machado">🪓 Machado (Vence Lança)</option>
                </select>
              </div>
            </div>

            <div className="admin-form-row">
              <label>Ícone (Índice no IconSet):</label>
              <input
                type="number"
                min="0"
                max="999"
                className="admin-input-number"
                value={icon}
                onChange={(e) => setIcon(Number(e.target.value))}
              />
            </div>

            <div className="admin-form-row">
              <label>Descrição / Texto de Ajuda:</label>
              <textarea
                className="admin-textarea"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex: [Lança] Forjada pelos monges de Oz para combater criaturas sombrias."
              />
            </div>
          </div>

          {/* Caixa 2: Estatísticas de Combate */}
          <div className="admin-section-box">
            <h3 className="admin-section-title">Parâmetros de Combate</h3>

            <div className="admin-grid-2col">
              <div className="admin-form-row">
                <label>Poder (Might / Dano Base):</label>
                <input
                  type="number"
                  min="0"
                  max="60"
                  className="admin-input-number"
                  value={might}
                  onChange={(e) => setMight(Number(e.target.value))}
                />
              </div>

              <div className="admin-form-row">
                <label>Precisão Base (Hit %):</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="admin-input-number"
                  value={hit}
                  onChange={(e) => setHit(Number(e.target.value))}
                />
              </div>

              <div className="admin-form-row">
                <label>Chance de Crítico (Crit %):</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="admin-input-number"
                  value={crit}
                  onChange={(e) => setCrit(Number(e.target.value))}
                />
              </div>

              <div className="admin-form-row">
                <label>Peso da Arma (Weight):</label>
                <input
                  type="number"
                  min="0"
                  max="30"
                  className="admin-input-number"
                  value={weight}
                  onChange={(e) => setWeight(Number(e.target.value))}
                />
                <span className="admin-helper-text">
                  Reduz a velocidade de ataque se ultrapassar a Con do combatente.
                </span>
              </div>
            </div>

            <div className="admin-grid-2col" style={{ marginTop: '12px' }}>
              <div className="admin-form-row">
                <label>Alcance Mínimo:</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  className="admin-input-number"
                  value={minRange}
                  onChange={(e) => setMinRange(Number(e.target.value))}
                />
              </div>

              <div className="admin-form-row">
                <label>Alcance Máximo:</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  className="admin-input-number"
                  value={maxRange}
                  onChange={(e) => setMaxRange(Number(e.target.value))}
                />
              </div>
            </div>
          </div>

          {/* Caixa 3: Propriedades Especiais e Eficácia */}
          <div className="admin-section-box admin-section-span2">
            <h3 className="admin-section-title">Propriedades & Eficácia</h3>

            <div className="admin-grid-2col">
              <div>
                <div className="admin-form-row">
                  <label className="admin-checkbox-label">
                    <input
                      type="checkbox"
                      checked={magic}
                      onChange={(e) => setMagic(e.target.checked)}
                    />
                    <strong>✨ Arma Mágica</strong>
                    <span className="admin-helper-text">
                      (Usa MAG do usuário e calcula dano contra a RES do alvo)
                    </span>
                  </label>
                </div>

                <div className="admin-form-row">
                  <label className="admin-checkbox-label">
                    <input
                      type="checkbox"
                      checked={heals}
                      onChange={(e) => setHeals(e.target.checked)}
                    />
                    <strong>💚 Item de Cura / Cajado</strong>
                    <span className="admin-helper-text">
                      (Restaura pontos de vida de aliados ao invés de causar dano)
                    </span>
                  </label>
                </div>
              </div>

              <div className="admin-grid-2col">
                <div className="admin-form-row">
                  <label>Usos / Durabilidade:</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    className="admin-input-number"
                    value={uses}
                    onChange={(e) => setUses(Number(e.target.value))}
                  />
                </div>

                <div className="admin-form-row">
                  <label>Preço em Ouro (Gold):</label>
                  <input
                    type="number"
                    min="0"
                    max="50000"
                    className="admin-input-number"
                    value={price}
                    onChange={(e) => setPrice(Number(e.target.value))}
                  />
                </div>
              </div>
            </div>

            <div className="admin-form-row" style={{ marginTop: '16px' }}>
              <label>Arma Eficaz Contra (Dano de Arma ×3):</label>
              <div className="admin-checkbox-group">
                {EFFECTIVE_TARGETS.map((eff) => (
                  <label key={eff.id} className="admin-checkbox-label">
                    <input
                      type="checkbox"
                      checked={effectiveAgainst.includes(eff.id)}
                      onChange={() => toggleEffective(eff.id)}
                    />
                    <span>{eff.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* O que o golpe deixa no alvo — e, no cajado, o que ele tira. */}
            <div className="admin-grid-2col" style={{ marginTop: '16px' }}>
              <div className="admin-form-row">
                <label>Causa a condição (a cada golpe que acerta):</label>
                <select
                  className="admin-select"
                  value={inflictCondition}
                  onChange={(e) => setInflictCondition(e.target.value)}
                >
                  <option value="">— nenhuma —</option>
                  {CONDITION_OPTIONS.map((c) => (
                    <option key={c.id} value={c.id} title={c.summary}>
                      {c.label} ({c.turns} {c.turns === 1 ? 'turno' : 'turnos'})
                    </option>
                  ))}
                </select>
                {inflictCondition !== '' && (
                  <>
                    <label style={{ marginTop: '8px' }}>Chance por golpe (%):</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      className="admin-input-number"
                      value={inflictChance}
                      onChange={(e) => setInflictChance(Number(e.target.value) || 1)}
                    />
                    <span className="admin-helper-text">
                      O sorteio é por golpe certeiro, e só em quem sobrevive a ele.
                      Metade dos golpes já é muito: a barra de previsão mostra a chance ao jogador.
                    </span>
                  </>
                )}
              </div>

              {heals && (
                <div className="admin-form-row">
                  <label>Cura as condições (de quem o cajado atende):</label>
                  <div className="admin-checkbox-group">
                    {CONDITION_OPTIONS.map((c) => (
                      <label key={c.id} className="admin-checkbox-label" title={c.summary}>
                        <input
                          type="checkbox"
                          checked={cures.includes(c.id)}
                          onChange={(e) =>
                            setCures((current) =>
                              e.target.checked
                                ? [...current, c.id]
                                : current.filter((id) => id !== c.id),
                            )
                          }
                        />
                        <span>{c.label}</span>
                      </label>
                    ))}
                  </div>
                  <span className="admin-helper-text">
                    Um cajado que cura condição atende também quem está de PV cheio.
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
